import { CLASS_BARS, type ClassBars } from "./autonomy";
import {
  evaluateApprovalHalf,
  evaluateGraduation,
  type ApprovalHalfBlocker,
  type EditKind,
  type GraduationBlocker,
  type GraduationEvidenceGap,
  type GraduationResult,
  type GraduationWarning,
  type ObservationSpan,
  type RedTeamStatus,
} from "./graduation";
import type { GraduationClass } from "./types";

/**
 * TA-187, Agent Brains section 5.9 (the Learning agent). `apps/web/src/lib/
 * agents.ts`'s `learning` entry, verbatim: mission "Turn what worked into
 * what the agents do next, slowly and with proof."; never "Never edits
 * guardrails, prices, compliance rules, or its own objective."; handoff
 * "Reads the ledger. Proposes; you approve."; produces "Pattern proposals
 * with citations, and graduation recommendations."
 *
 * SPEC_COVERAGE.md's own B6 row names the exact gap this file closes,
 * verbatim: "The eval half is computed from `mockJewelerRespond`, a
 * deterministic stand-in... The approval half is stored per draft
 * (`edit_kind`, `owner_flagged_substantive`) and no code assembles a
 * per-agent record out of it, and nothing persists a standing: the state
 * machine is pure and unwired, which is the skeleton step 6 asked for and
 * not a working graduation."
 *
 * `graduation.ts`'s `evaluateGraduation` already computes the eligibility
 * decision correctly from a `GraduationInput`. Nothing here re-derives
 * F-04, F-05 or F-25; every rule stays exactly where it already lives. What
 * was missing, confirmed by grep before writing a line (`evaluateGraduation`
 * had exactly one caller anywhere in this codebase: its own test file), was
 * the assembly step between "drafts the owner has decided on" and "the
 * shape `evaluateGraduation` wants". This file is that assembly, split the
 * same way `leadCard.ts` splits pure assembly from a DB read: `packages/
 * agents/src/graduationRecord.ts` is the thin loader that turns real
 * `drafts` rows into `DraftDecisionRow[]` and calls straight through.
 *
 * WHAT THIS FILE DELIBERATELY DOES NOT DO. It never touches the OTHER half
 * of that SPEC_COVERAGE sentence, "nothing persists a standing": it writes
 * nothing, computes no `AutonomyStanding`, and never calls
 * `applyAutonomyTrigger`. Persisting a standing needs a durable table and a
 * write path gated on a named human (`autonomy.ts`'s own `human_promotion`
 * trigger requires `by`), which is a second, separate, riskier piece of
 * machinery this task did not build; see FINDINGS.md TA-187 for the full
 * reasoning. This file produces a `GraduationRecord`: evidence a human
 * reads before flipping a switch that lives entirely outside this file.
 *
 * "THE EVAL HALF IS COMPUTED FROM A MOCK." That sentence in SPEC_COVERAGE
 * is still true and stays true here: nothing in this repo measures
 * `worstOfKPassed` or first-pass grounding against a real model for a real
 * agent yet (`packages/evals` scores `mockJewelerRespond`). So the eval
 * half is an explicit, optional input (`EvalHalfInput`) a caller supplies
 * when it has one, never invented, never defaulted to a pass. Missing it
 * reports `not_yet_measured`, never silently treated as measured-and-clean:
 * section 0, "when unsure, don't".
 */

/**
 * How many rejected draft ids a `GraduationRecord` shows as evidence a
 * human can open and read. Chosen independently of, but the same value as,
 * `outcomeDigest.ts`'s `DIGEST_LIST_CAP`: "never more than a few taps of
 * debt" is that module's own illustration, and rejected-draft review is the
 * same shape of debt, a short reading list plus an honest total. The total
 * (`rejectedCount`) and the overflow (`rejectedOverflow`) are never capped,
 * only the rendered sample: a record that shows 10 of 47 and says 10 is
 * exactly section 0's silent degradation. AWAITING RATIFICATION, like
 * `DIGEST_LIST_CAP` before it.
 */
export const GRADUATION_SAMPLE_CAP = 10; // AWAITING RATIFICATION

export interface DraftDecisionRow {
  readonly draftId: string;
  readonly status: "approved" | "rejected";
  /** Null for a rejected draft: F-05's classification only ever runs on an
   * approval (`decideApprove`, `packages/db/src/approvals.ts`). */
  readonly editKind: EditKind | null;
  /** F-25: was this clean before the corrective retry. */
  readonly firstPassClean: boolean;
  /**
   * TA-1266 (F-25 data only): the first pass's verdict carried the Voice
   * gate's `voice_off_band` (R7b), read from `first_pass_verifier_result`.
   * Absent or false when not refused on voice or not recorded. It feeds
   * `voiceRepairedDrafts` alone and changes no other count.
   */
  readonly firstPassVoiceRefused?: boolean;
  /**
   * R7b fix 2 (N7, F-25 data only): the first pass was not clean and its stored
   * verdict could not be read (absent, or not an object with a `violations`
   * list), so whether voice refused it is unknown. Feeds
   * `voiceUnrecordedDrafts` alone.
   */
  readonly firstPassVerdictUnrecorded?: boolean;
  /**
   * The escalation sweep's fixed line (decision list items 78a and 84a): no
   * model wrote it, so it is not the agent's draft and counts toward nothing
   * (TA-467). False when not recorded (drafts before migration 0034).
   */
  readonly holdingLine: boolean;
  readonly decidedAt: Date;
}

export interface ApprovalRecord {
  /** Every drafted reply the owner has decided on, approved or rejected. */
  readonly draftsReviewed: number;
  /** F-05: unedited + cosmetic, never substantive. */
  readonly approvedWithoutEdit: number;
  /** `approvedWithoutEdit / draftsReviewed`, 0 on no data, never NaN. */
  readonly approvalRate: number;
  readonly rejectedCount: number;
  readonly editKindCounts: Readonly<Record<EditKind, number>>;
  /** F-25's early-warning signal: needed a corrective retry, over ALL
   * decided drafts, not only the approved ones. Rounded to three decimals
   * for reading, so it can show a rate over the bar as exactly the bar
   * (21 of 209 shows 0.1). Never decide anything on it: see `repairedDrafts`. */
  readonly repairRate: number;
  /** TA-241: the exact count behind `repairRate`, decided drafts that were not
   * first-pass clean. `evaluateGraduation` decides F-25's warning on this and
   * `draftsReviewed`. */
  readonly repairedDrafts: number;
  /**
   * TA-1266 (F-25 data, no behaviour change): of `repairedDrafts`, the ones
   * whose first pass the Voice gate refused (R7b), alone or beside another
   * code. Since R7b a voice refusal is a first-pass failure, so it already
   * sits inside `repairedDrafts`, `repairRate` and the approval rate; this
   * count only makes that share visible, so Kabir can decide whether voice
   * belongs in F-25's repair and grounding figures (review r7b I4). Nothing
   * reads it to decide anything, and what the existing counters count is
   * unchanged.
   */
  readonly voiceRepairedDrafts: number;
  /**
   * R7b fix 2 (N7, F-25 data, no behaviour change): of `repairedDrafts`, the
   * ones whose first verdict could not be read, so `voiceRepairedDrafts` is a
   * floor by this much and never a silent miscount (section 0; the approval
   * counterpart of shadowExit's `firstPassUnrecorded`). A first attempt that
   * never parsed lands here too: it never reached the gate. Nothing reads it to
   * decide anything.
   */
  readonly voiceUnrecordedDrafts: number;
  /** Most recent first, capped at `GRADUATION_SAMPLE_CAP`. */
  readonly sampleRejectedDraftIds: readonly string[];
  /** What the cap hid. Zero when every rejection is already shown. */
  readonly rejectedOverflow: number;
  /** Decided holding lines, set aside from every count above (TA-467). */
  readonly holdingLinesSetAside: number;
  /**
   * B6 "over 2+ weeks": the earliest and latest decision this record rests
   * on, ISO strings. Null exactly when there are no decided drafts. Carried
   * on the record so the human reading it sees the span, not only a verdict.
   * It says nothing about spread: one old decision anchors it however the
   * rest are bunched (the known gap logged for Kabir, B8).
   */
  readonly spanStart: string | null;
  readonly spanEnd: string | null;
}

const EMPTY_EDIT_KIND_COUNTS: Record<EditKind, number> = {
  unedited: 0,
  cosmetic: 0,
  substantive: 0,
};

/**
 * The approval half, assembled from real decided drafts. Pure: takes rows a
 * caller already read, invents nothing about them.
 */
export function buildApprovalRecord(allRows: readonly DraftDecisionRow[]): ApprovalRecord {
  const rows = allRows.filter((r) => !r.holdingLine);
  const draftsReviewed = rows.length;
  const editKindCounts: Record<EditKind, number> = { ...EMPTY_EDIT_KIND_COUNTS };
  let approvedWithoutEdit = 0;
  let rejectedCount = 0;
  let repaired = 0;
  // TA-1266: counted beside `repaired`, never instead of it.
  let voiceRepaired = 0;
  let voiceUnrecorded = 0;
  const rejectedIds: { draftId: string; decidedAt: Date }[] = [];
  let spanStartMs = Number.POSITIVE_INFINITY;
  let spanEndMs = Number.NEGATIVE_INFINITY;

  for (const r of rows) {
    if (!r.firstPassClean) repaired += 1;
    if (!r.firstPassClean && r.firstPassVoiceRefused === true) voiceRepaired += 1;
    if (!r.firstPassClean && r.firstPassVerdictUnrecorded === true) voiceUnrecorded += 1;
    // `drafts.decided_at` is nullable in the schema. A decided draft without a
    // valid one cannot be placed on the B6 span, and a bare TypeError would
    // name neither the draft nor the fix (section 0, fail loudly).
    const decidedMs = r.decidedAt instanceof Date ? r.decidedAt.getTime() : Number.NaN;
    if (Number.isNaN(decidedMs)) {
      throw new Error(
        `buildApprovalRecord: draft ${r.draftId} is ${r.status} but has no valid decided_at (${String(r.decidedAt)}). ` +
          "A decided draft must carry the instant it was decided; fix the row before assembling a graduation record.",
      );
    }
    // Every decided draft is evidence, rejected ones included: they are part
    // of `draftsReviewed`, so they are part of the span it covers.
    spanStartMs = Math.min(spanStartMs, decidedMs);
    spanEndMs = Math.max(spanEndMs, decidedMs);

    if (r.status === "rejected") {
      rejectedCount += 1;
      rejectedIds.push({ draftId: r.draftId, decidedAt: r.decidedAt });
      continue;
    }

    if (r.editKind) {
      editKindCounts[r.editKind] += 1;
      // F-25: the approval rate is measured on first-pass outputs, never
      // post-retry. A draft that needed the corrective retry stays in
      // `draftsReviewed` and in `editKindCounts` (it was decided, and `repaired`
      // counts it) but is not credited here (TA-569).
      if (r.editKind !== "substantive" && r.firstPassClean) approvedWithoutEdit += 1;
    }
  }

  const approvalRate = draftsReviewed === 0 ? 0 : approvedWithoutEdit / draftsReviewed;
  const repairRate = draftsReviewed === 0 ? 0 : repaired / draftsReviewed;

  // Most recent first: the evidence a human wants when reading "what got
  // rejected" is what just happened, not the oldest thing on record.
  const sortedRejected = [...rejectedIds].sort(
    (a, b) => b.decidedAt.getTime() - a.decidedAt.getTime(),
  );

  return {
    draftsReviewed,
    approvedWithoutEdit,
    approvalRate: Math.round(approvalRate * 1000) / 1000,
    rejectedCount,
    editKindCounts,
    repairRate: Math.round(repairRate * 1000) / 1000,
    repairedDrafts: repaired,
    voiceRepairedDrafts: voiceRepaired,
    voiceUnrecordedDrafts: voiceUnrecorded,
    sampleRejectedDraftIds: sortedRejected.slice(0, GRADUATION_SAMPLE_CAP).map((r) => r.draftId),
    rejectedOverflow: Math.max(0, sortedRejected.length - GRADUATION_SAMPLE_CAP),
    holdingLinesSetAside: allRows.length - rows.length,
    spanStart: draftsReviewed === 0 ? null : new Date(spanStartMs).toISOString(),
    spanEnd: draftsReviewed === 0 ? null : new Date(spanEndMs).toISOString(),
  };
}

/**
 * B8. Nothing in this codebase records a red-team result against an agent at
 * a version: `packages/evals` runs its scripted attacks per eval run and keeps
 * no store a graduation record could read. Until one exists every assembled
 * record carries `not_recorded`, which `evaluateGraduation` blocks on (B7:
 * "before any scenario graduates"). Deliberately not an input: a caller with
 * no store has no honest value to pass, and an input invites `"clean"`.
 */
const RED_TEAM_WITHOUT_A_STORE: RedTeamStatus = "not_recorded";

/**
 * Never "measured_eligible" or any word that could be misread as a
 * decision: this is a status about DATA COMPLETENESS, and `graduation`
 * carries the actual (non-)decision.
 */
export type GraduationEvalStatus = "measured" | "not_yet_measured" | "class_never_graduates";

export interface EvalHalfInput {
  readonly worstOfKPassed: boolean;
  /** FIRST-PASS grounding, never post-retry (F-25). Same field name and
   * warning as `GraduationInput.firstPassGroundingScore`. */
  readonly firstPassGroundingScore: number;
  readonly groundingFloor: number;
}

export interface GraduationRecord {
  readonly agentId: string;
  readonly graduationClass: GraduationClass;
  /** Read straight off `autonomy.ts`'s own `CLASS_BARS`, never re-derived. */
  readonly classBars: ClassBars;
  readonly approval: ApprovalRecord;
  readonly evalStatus: GraduationEvalStatus;
  /** Null exactly when `evalStatus` is not `"measured"`. Never a fabricated
   * pass or fail standing in for a measurement that was never taken. */
  readonly graduation: GraduationResult | null;
  /** B7 red-team gate as this record saw it. See `RED_TEAM_WITHOUT_A_STORE`. */
  readonly redTeam: RedTeamStatus;
  readonly generatedAt: string;
}

export interface BuildGraduationRecordInput {
  readonly agentId: string;
  readonly graduationClass: GraduationClass;
  readonly approval: ApprovalRecord;
  /** Null when no real measurement exists yet for this agent. See the file
   * doc comment: this is deliberately never defaulted to a pass. */
  readonly evalHalf: EvalHalfInput | null;
  readonly now?: Date;
}

/**
 * B6 "over 2+ weeks": the record's ISO span as the `ObservationSpan`
 * `observationSpanMet` reads, null when either end is missing. TA-333 moved it
 * here from `buildGraduationRecord` so `knownGraduationBlockers` converts the
 * span the same way, not a second way.
 */
function observationSpanOf(approval: ApprovalRecord): ObservationSpan | null {
  return approval.spanStart === null || approval.spanEnd === null
    ? null
    : { start: new Date(approval.spanStart), end: new Date(approval.spanEnd) };
}

/**
 * TA-333. What a `GraduationRecord` can say about its own blockers, by eval
 * status. Three shapes, and the type is the point:
 *
 *   measured               the record's own `evaluateGraduation` result,
 *                          complete, and the only shape carrying `eligible`.
 *   not_yet_measured       the approval half's findings plus
 *                          `eval_not_measured`, marked incomplete, and with
 *                          no `eligible` field at all: a reader cannot read a
 *                          verdict off a list that was never a verdict.
 *   class_never_graduates  no list. Class D keeps a human in the loop at any
 *                          volume, so listing what "blocks" it would imply
 *                          something could clear it.
 */
export type KnownGraduationBlockers =
  | {
      readonly status: "measured";
      readonly complete: true;
      readonly eligible: boolean;
      readonly decision: GraduationResult["decision"];
      readonly blockers: readonly GraduationBlocker[];
      readonly warnings: readonly string[];
    }
  | {
      readonly status: "not_yet_measured";
      readonly complete: false;
      /** The approval half's blockers in `evaluateGraduation`'s order, then `eval_not_measured`, always last. */
      readonly blockers: readonly (ApprovalHalfBlocker | GraduationEvidenceGap)[];
      readonly warnings: readonly GraduationWarning[];
    }
  | { readonly status: "class_never_graduates" };

/**
 * TA-333. Every real record today has `graduation: null`, because nothing
 * measures a real eval half, so a record carried no blockers and the human
 * B6 asks to flip the switch had nothing to read. This reports what is known:
 * the approval half's findings, computed from `record.approval`,
 * `record.classBars` and `record.redTeam` through `evaluateApprovalHalf`, the
 * same function `evaluateGraduation` calls, so a record read now can never
 * disagree with the verdict it gets once the eval half is measured.
 *
 * NEVER a verdict when the eval half is missing (F-04: both bars, never
 * either). A record whose status and result disagree, or an unmeasured class
 * with no numeric bars, throws rather than reporting a guess (section 0).
 */
export function knownGraduationBlockers(record: GraduationRecord): KnownGraduationBlockers {
  switch (record.evalStatus) {
    case "measured": {
      if (record.graduation === null) {
        throw new Error(
          `knownGraduationBlockers: record for "${record.agentId}" class "${record.graduationClass}" says measured but carries no graduation result`,
        );
      }
      return {
        status: "measured",
        complete: true,
        eligible: record.graduation.eligible,
        decision: record.graduation.decision,
        blockers: record.graduation.blockers,
        warnings: record.graduation.warnings,
      };
    }
    case "class_never_graduates":
      // The same refusal as the other two arms: a record whose status and
      // result disagree is a caller bug, never read as either one.
      if (record.graduation !== null) {
        throw new Error(
          `knownGraduationBlockers: record for "${record.agentId}" class "${record.graduationClass}" never graduates but carries a graduation result`,
        );
      }
      return { status: "class_never_graduates" };
    case "not_yet_measured": {
      const bars = record.classBars;
      if (record.graduation !== null || !bars.graduates || bars.minDrafts === null || bars.approvalFloor === null) {
        throw new Error(
          `knownGraduationBlockers: record for "${record.agentId}" class "${record.graduationClass}" is not_yet_measured but ` +
            (record.graduation !== null ? "carries a graduation result" : "its class has no numeric bars to read against"),
        );
      }
      const found = evaluateApprovalHalf({
        draftsReviewed: record.approval.draftsReviewed,
        approvedWithoutEdit: record.approval.approvedWithoutEdit,
        minDrafts: bars.minDrafts,
        approvalFloor: bars.approvalFloor,
        // TA-241: the count, never the rounded `repairRate`.
        repairedDrafts: record.approval.repairedDrafts,
        observationSpan: observationSpanOf(record.approval),
        redTeam: record.redTeam,
      });
      return {
        status: "not_yet_measured",
        complete: false,
        blockers: [...found.blockers, "eval_not_measured"],
        warnings: found.warnings,
      };
    }
    default: {
      const exhaustive: never = record.evalStatus;
      throw new Error(`knownGraduationBlockers: unhandled eval status "${String(exhaustive)}"`);
    }
  }
}

/** `GraduationResult.decision`'s own two values, copied here only to guard
 * against graduation.ts's union ever growing a third, promotive value (see
 * the throw below). Not a redefinition: this file never re-derives what
 * counts as eligible, only refuses to report a decision it does not
 * recognise. */
const KNOWN_GRADUATION_DECISIONS = new Set<GraduationResult["decision"]>([
  "eligible_pending_human",
  "not_eligible",
]);

/**
 * Assembles a per-agent `GraduationRecord`: real approval evidence plus
 * (when supplied) a real eval measurement, run through `evaluateGraduation`
 * unchanged. NEVER a write. NEVER graduates, promotes, or persists a
 * standing. B6, verbatim: "Thresholds qualify; the human flips the switch,
 * always." This function's entire job ends at "thresholds qualify".
 */
export function buildGraduationRecord(input: BuildGraduationRecordInput): GraduationRecord {
  const now = input.now ?? new Date();
  const classBars = CLASS_BARS[input.graduationClass];

  // Exhaustive, `routeDestination.ts`'s own discipline: a fifth
  // `GraduationClass` without a case here is a compile error, not a silent
  // fall-through into a fabricated eligibility.
  switch (input.graduationClass) {
    case "A":
    case "B":
    case "C":
      break;
    case "D":
      // Section 9 / autonomy.ts: "D = never fully automated (human always
      // in loop)". No volume of drafts changes that, so this never even
      // reaches `evaluateGraduation`.
      return {
        agentId: input.agentId,
        graduationClass: input.graduationClass,
        classBars,
        approval: input.approval,
        evalStatus: "class_never_graduates",
        graduation: null,
        redTeam: RED_TEAM_WITHOUT_A_STORE,
        generatedAt: now.toISOString(),
      };
    default: {
      const exhaustive: never = input.graduationClass;
      throw new Error(
        `buildGraduationRecord: unhandled graduation class "${String(exhaustive)}"`,
      );
    }
  }

  // Belt and suspenders on the exhaustive switch above (section 0, "fail
  // loudly"): today only class D lacks numeric bars, but a future edit to
  // `CLASS_BARS` must not silently start reporting a fabricated eligibility
  // for a class the map itself says has none.
  if (!classBars.graduates || classBars.minDrafts === null || classBars.approvalFloor === null) {
    throw new Error(
      `buildGraduationRecord: class "${input.graduationClass}" has no numeric bars to evaluate against`,
    );
  }

  if (input.evalHalf === null) {
    return {
      agentId: input.agentId,
      graduationClass: input.graduationClass,
      classBars,
      approval: input.approval,
      evalStatus: "not_yet_measured",
      graduation: null,
      redTeam: RED_TEAM_WITHOUT_A_STORE,
      generatedAt: now.toISOString(),
    };
  }

  const graduation = evaluateGraduation({
    scenario: input.agentId,
    graduationClass: input.graduationClass,
    draftsReviewed: input.approval.draftsReviewed,
    approvedWithoutEdit: input.approval.approvedWithoutEdit,
    minDrafts: classBars.minDrafts,
    approvalFloor: classBars.approvalFloor,
    worstOfKPassed: input.evalHalf.worstOfKPassed,
    firstPassGroundingScore: input.evalHalf.firstPassGroundingScore,
    groundingFloor: input.evalHalf.groundingFloor,
    // TA-241: the count, never `approval.repairRate`, which is rounded for
    // reading and hid 21 of 209 as exactly the bar.
    repairedDrafts: input.approval.repairedDrafts,
    observationSpan: observationSpanOf(input.approval),
    redTeam: RED_TEAM_WITHOUT_A_STORE,
  });

  // Unreachable today, `GraduationResult.decision` is a two-value union
  // imported unchanged from `graduation.ts`. Kept anyway: section 0 is
  // "fail loudly", not "trust the type checker forever". If that union ever
  // grows a third, promotive value, this throws instead of quietly calling
  // an agent "graduated".
  if (!KNOWN_GRADUATION_DECISIONS.has(graduation.decision)) {
    throw new Error(
      `buildGraduationRecord: unrecognised graduation decision "${graduation.decision}", refusing to report it`,
    );
  }

  return {
    agentId: input.agentId,
    graduationClass: input.graduationClass,
    classBars,
    approval: input.approval,
    evalStatus: "measured",
    graduation,
    redTeam: RED_TEAM_WITHOUT_A_STORE,
    generatedAt: now.toISOString(),
  };
}
